import React, { useEffect, useState, useRef } from 'react';

/**
 * AnimatedCounter component - animates stats from left to right when scrolling
 * Animation speed is based on scroll velocity
 */
const AnimatedCounter = ({ stats }) => {
    const [visibleItems, setVisibleItems] = useState([]);
    const [scrollVelocity, setScrollVelocity] = useState(1);
    const sectionRef = useRef(null);
    const lastScrollY = useRef(0);
    const lastScrollTime = useRef(Date.now());
    const hasAnimated = useRef(false);

    // Track scroll velocity
    useEffect(() => {
        const handleScroll = () => {
            const currentTime = Date.now();
            const currentScrollY = window.scrollY;
            const timeDiff = currentTime - lastScrollTime.current;
            const scrollDiff = Math.abs(currentScrollY - lastScrollY.current);

            // Calculate velocity (pixels per millisecond)
            const velocity = timeDiff > 0 ? scrollDiff / timeDiff : 0;

            // Normalize velocity to a scale of 0.2 to 2 (slower = longer delay, faster = shorter delay)
            const normalizedVelocity = Math.max(0.2, Math.min(2, velocity * 2));
            setScrollVelocity(normalizedVelocity);

            lastScrollY.current = currentScrollY;
            lastScrollTime.current = currentTime;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Intersection observer to trigger animation when section comes into view
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !hasAnimated.current) {
                        hasAnimated.current = true;
                        // Animate items from left to right with staggered delay based on scroll velocity
                        stats.forEach((_, index) => {
                            // Base delay adjusted by scroll velocity (faster scroll = faster animation)
                            const baseDelay = 150; // ms between each item
                            const adjustedDelay = baseDelay / scrollVelocity;

                            setTimeout(() => {
                                setVisibleItems((prev) => [...prev, index]);
                            }, index * adjustedDelay);
                        });
                    }
                });
            },
            { threshold: 0.3 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, [stats, scrollVelocity]);

    // Counter animation for numbers
    const AnimatedNumber = ({ value, isVisible }) => {
        const [displayValue, setDisplayValue] = useState(0);
        const numericValue = parseInt(value.replace(/[^0-9]/g, ''), 10);
        const suffix = value.replace(/[0-9]/g, '');

        useEffect(() => {
            if (!isVisible) return;

            const duration = 1500 / scrollVelocity; // Faster scroll = faster count
            const steps = 60;
            const increment = numericValue / steps;
            let current = 0;
            let step = 0;

            const timer = setInterval(() => {
                step++;
                current = Math.min(Math.round(increment * step), numericValue);
                setDisplayValue(current);

                if (step >= steps) {
                    clearInterval(timer);
                    setDisplayValue(numericValue);
                }
            }, duration / steps);

            return () => clearInterval(timer);
        }, [isVisible, numericValue, scrollVelocity]);

        return (
            <span>
                {isVisible ? displayValue : 0}{suffix}
            </span>
        );
    };

    return (
        <section
            ref={sectionRef}
            className="py-20 border-y border-white/5 bg-black/20 backdrop-blur-sm"
        >
            <div className="container mx-auto px-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                    {stats.map((stat, index) => {
                        const isVisible = visibleItems.includes(index);
                        return (
                            <div
                                key={index}
                                className={`text-center group transform transition-all duration-700 ${isVisible
                                        ? 'opacity-100 translate-x-0 scale-100'
                                        : 'opacity-0 -translate-x-12 scale-90'
                                    }`}
                                style={{
                                    transitionDelay: isVisible ? '0ms' : `${index * 100}ms`,
                                }}
                            >
                                <div className="text-4xl md:text-5xl font-mono font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-2 group-hover:scale-110 transition-transform duration-300">
                                    <AnimatedNumber value={stat.value} isVisible={isVisible} />
                                </div>
                                <div className="text-slate-400 text-sm uppercase tracking-wider font-semibold">
                                    {stat.label}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default AnimatedCounter;
